import React, { useEffect, useRef } from 'react';

/**
 * AnimatedGradientBackground - Flowing lines with dynamic contrast and sharpness
 *
 * Features:
 * - Flowing wave lines that move across the screen
 * - Dynamic contrast that shifts from high to low
 * - Variable sharpness using blur effects
 * - Rotation and transformation animations
 */
const AnimatedGradientBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;

    // Set canvas size
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasSize();

    // Handle window resize
    const handleResize = () => {
      setCanvasSize();
    };
    window.addEventListener('resize', handleResize);

    // Color palettes for the flowing lines
    const colorPalettes = [
      // Blue tones
      [
        { r: 59, g: 130, b: 246 },   // Blue
        { r: 99, g: 102, b: 241 },   // Indigo
        { r: 139, g: 92, b: 246 },   // Purple
      ],
      // Teal-Cyan
      [
        { r: 20, g: 184, b: 166 },   // Teal
        { r: 34, g: 211, b: 238 },   // Cyan
        { r: 14, g: 165, b: 233 },   // Sky
      ],
      // Pink-Purple
      [
        { r: 236, g: 72, b: 153 },   // Pink
        { r: 168, g: 85, b: 247 },   // Violet
        { r: 244, g: 114, b: 182 },  // Light Pink
      ],
      // Green-Emerald
      [
        { r: 16, g: 185, b: 129 },   // Green
        { r: 52, g: 211, b: 153 },   // Emerald
        { r: 45, g: 212, b: 191 },   // Turquoise
      ],
    ];

    // Get color with cycling
    const getColor = (baseIndex, time) => {
      const cycleSpeed = 0.00008;
      const paletteIndex = Math.floor((time * cycleSpeed) % colorPalettes.length);
      const nextPaletteIndex = (paletteIndex + 1) % colorPalettes.length;
      const blendFactor = (time * cycleSpeed) % 1;

      const currentPalette = colorPalettes[paletteIndex];
      const nextPalette = colorPalettes[nextPaletteIndex];

      const colorIndex = baseIndex % currentPalette.length;
      const currentColor = currentPalette[colorIndex];
      const nextColor = nextPalette[colorIndex];

      return {
        r: Math.floor(currentColor.r + (nextColor.r - currentColor.r) * blendFactor),
        g: Math.floor(currentColor.g + (nextColor.g - currentColor.g) * blendFactor),
        b: Math.floor(currentColor.b + (nextColor.b - currentColor.b) * blendFactor),
      };
    };

    // Flowing line class
    class FlowingLine {
      constructor(index, totalLines) {
        this.index = index;
        this.totalLines = totalLines;
        this.baseY = (canvas.height / (totalLines + 1)) * (index + 1);
        this.amplitude = 80 + Math.random() * 120;
        this.frequency = 0.002 + Math.random() * 0.003;
        this.speed = 0.0003 + Math.random() * 0.0005;
        this.thickness = 3 + Math.random() * 8;
        this.colorIndex = index % 3;
        this.rotationSpeed = (Math.random() - 0.5) * 0.00015;
        this.rotation = 0;
        this.phaseOffset = Math.random() * Math.PI * 2;
        this.contrastCycleSpeed = 0.0001 + Math.random() * 0.00015;
        this.sharpnessCycleSpeed = 0.00008 + Math.random() * 0.00012;
      }

      getContrast(time) {
        // Oscillate between high contrast (0.9) and low contrast (0.15)
        const cycle = Math.sin(time * this.contrastCycleSpeed + this.phaseOffset);
        return 0.15 + (cycle * 0.5 + 0.5) * 0.75;
      }

      getSharpness(time) {
        // Oscillate between sharp (0) and blurry (25)
        const cycle = Math.sin(time * this.sharpnessCycleSpeed + this.phaseOffset + Math.PI);
        return (cycle * 0.5 + 0.5) * 25;
      }

      draw(ctx, time) {
        const color = getColor(this.colorIndex, time);
        const contrast = this.getContrast(time);
        const sharpness = this.getSharpness(time);

        ctx.save();

        // Apply rotation to the entire canvas for this line
        ctx.translate(canvas.width / 2, canvas.height / 2);
        this.rotation += this.rotationSpeed;
        ctx.rotate(this.rotation);
        ctx.translate(-canvas.width / 2, -canvas.height / 2);

        // Set blur based on sharpness
        ctx.filter = `blur(${sharpness}px)`;

        // Draw the flowing wave line
        ctx.beginPath();
        ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${contrast})`;
        ctx.lineWidth = this.thickness;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        const points = 200;
        const step = canvas.width / points;

        for (let i = 0; i <= points; i++) {
          const x = i * step;

          // Create flowing wave pattern
          const wave1 = Math.sin(x * this.frequency + time * this.speed + this.phaseOffset);
          const wave2 = Math.cos(x * this.frequency * 0.5 + time * this.speed * 0.7 + this.phaseOffset);
          const wave3 = Math.sin(x * this.frequency * 1.5 + time * this.speed * 0.5);

          const y = this.baseY +
                    wave1 * this.amplitude +
                    wave2 * this.amplitude * 0.5 +
                    wave3 * this.amplitude * 0.3;

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.stroke();

        // Add a second parallel line with gradient for more depth
        const gradient = ctx.createLinearGradient(0, this.baseY - 50, 0, this.baseY + 50);
        gradient.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
        gradient.addColorStop(0.5, `rgba(${color.r}, ${color.g}, ${color.b}, ${contrast * 0.3})`);
        gradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = this.thickness * 3;

        ctx.beginPath();
        for (let i = 0; i <= points; i++) {
          const x = i * step;
          const wave1 = Math.sin(x * this.frequency + time * this.speed + this.phaseOffset + 0.2);
          const wave2 = Math.cos(x * this.frequency * 0.5 + time * this.speed * 0.7 + this.phaseOffset + 0.2);
          const wave3 = Math.sin(x * this.frequency * 1.5 + time * this.speed * 0.5 + 0.2);

          const y = this.baseY +
                    wave1 * this.amplitude +
                    wave2 * this.amplitude * 0.5 +
                    wave3 * this.amplitude * 0.3;

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.stroke();

        ctx.restore();
      }
    }

    // Create multiple flowing lines
    const numLines = 12;
    const lines = [];
    for (let i = 0; i < numLines; i++) {
      lines.push(new FlowingLine(i, numLines));
    }

    // Animation loop
    const animate = () => {
      time += 1;

      // Create a subtle gradient background
      const bgGradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      bgGradient.addColorStop(0, '#f8fafc');
      bgGradient.addColorStop(0.5, '#f1f5f9');
      bgGradient.addColorStop(1, '#e2e8f0');

      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw all flowing lines
      lines.forEach(line => {
        line.draw(ctx, time);
      });

      // Add subtle noise
      ctx.globalAlpha = 0.012;
      for (let i = 0; i < canvas.width * canvas.height / 8000; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const brightness = Math.random() * 255;
        ctx.fillStyle = `rgb(${brightness}, ${brightness}, ${brightness})`;
        ctx.fillRect(x, y, 1, 1);
      }
      ctx.globalAlpha = 1;

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default AnimatedGradientBackground;
